import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { AlertDialog } from "@/shared/components";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { type CollectionRecord } from "@/shared/domain/collections";
import { collectionQueryKeys } from "@/shared/domain/collections/hooks/collectionQueryKeys";
import { useDeleteCollection } from "@/shared/domain/collections/hooks/useDeleteCollection";

import { CollectionsList } from "./CollectionsList";
import styles from "./CollectionsView.module.css";
import { CreateCollectionButton } from "./CreateCollectionButton";

export function CollectionsView() {
  const queryClient = useQueryClient();
  const orgId = useCurrentOrgId();
  const { mutateAsync: deleteCollection } = useDeleteCollection();
  const showNotification = useNotification();

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [collectionsToDelete, setCollectionsToDelete] = React.useState<
    CollectionRecord[]
  >([]);

  const selectionOptions: SelectionOption<CollectionRecord>[] = React.useMemo(
    () => [
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          setCollectionsToDelete(selected);
          setDeleteConfirmOpen(true);
        },
      },
    ],
    [],
  );

  const handleDeleteConfirm = async () => {
    const results = await Promise.allSettled(
      collectionsToDelete.map((c) =>
        deleteCollection({
          collectionId: c.collection_id,
          orgId: c.org_id,
          resourceOwnerId: c.org_id,
        }),
      ),
    );
    await queryClient.invalidateQueries({
      queryKey: collectionQueryKeys.lists(orgId),
    });
    const failures = results.filter((r) => r.status === "rejected").length;
    if (failures > 0) {
      showNotification(
        `${failures} collection${failures !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.list}>
        <CollectionsList
          actions={<CreateCollectionButton />}
          selectionOptions={selectionOptions}
        />
      </div>
      <AlertDialog
        dialogOpen={deleteConfirmOpen}
        handleClose={() => {
          setDeleteConfirmOpen(false);
          setCollectionsToDelete([]);
        }}
        dialogTitle={`Delete ${collectionsToDelete.length} collection${collectionsToDelete.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
}
